import React from "react";
import dayjs from "dayjs";
import { Box, Flex, Icon, Text, useColorModeValue } from "@chakra-ui/react";
import { MdBadge } from "react-icons/md";

/**
 * Formats a birth date as DD/MM/YYYY, or returns the raw value when it is not
 * a parseable date.
 * @param {string|null|undefined} birth - Declared birth date
 * @returns {string|null} Formatted date, raw string, or null when empty
 */
function formatBirth(birth) {
    if (!birth) return null;
    const parsed = dayjs(birth);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : String(birth);
}

/**
 * Single labeled identity field row.
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string|null} props.value - Field value
 * @returns {React.ReactElement}
 */
function IdentityRow({ label, value }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valueColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex align="baseline" justify="space-between" gap="12px" py="4px">
            <Text fontSize="xs" color={labelColor}>{label}</Text>
            <Text fontSize="sm" fontWeight="600" color={valueColor} textAlign="right">{value || "—"}</Text>
        </Flex>
    );
}

/**
 * Displays the declared identity (first name, last name, birth date) returned
 * by the API as `info_to_compare`, so the admin can cross-check it against the
 * uploaded documents during KYC review. Hidden when no identity data exists.
 * @param {Object} props
 * @param {Object} props.certification - Certification with optional info_to_compare
 * @returns {React.ReactElement|null}
 */
function IdentityCompare({ certification }) {
    const info = certification?.info_to_compare;
    const bg = useColorModeValue("blue.50", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    if (!info || (!info.name && !info.lastname && !info.birth)) return null;
    return (
        <Box p="14px" bg={bg} borderRadius="12px">
            <Flex align="center" gap="8px" mb="8px">
                <Icon as={MdBadge} w="18px" h="18px" color="#2196f3" />
                <Text fontSize="sm" fontWeight="700" color={titleColor}>Identité déclarée</Text>
            </Flex>
            <Text fontSize="xs" color={hintColor} mb="8px">À comparer avec les documents fournis.</Text>
            <IdentityRow label="Prénom" value={info.name} />
            <IdentityRow label="Nom" value={info.lastname} />
            <IdentityRow label="Date de naissance" value={formatBirth(info.birth)} />
        </Box>
    );
}

export default IdentityCompare;
